import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Icon from '@material-ui/core/Icon';
import I18n from 'i18n-js';
import { makeStyles } from '@material-ui/core/styles';
import { Menu, MenuItem } from '@material-ui/core';
import EmojiPicker from 'emoji-picker-react';
import { useTheme } from '@material-ui/core/styles';
import { Logger } from '@eyeson/js';
import useEventListenerRef from '../../../utils/useEventListenerRef.js';
import { getBasePath } from '../../../utils/UrlHelpers.js';

const getEmojiVersion = () => {
  const supportsOffscreen = typeof window.OffscreenCanvas === 'function';
  const emoji = {
    // '16.0': '🫟',
    // 15.1: '🙂‍↔️',
    // '15.0': '🫨',
    '14.0': '🫠',
    13.1: '😶‍🌫️',
    '13.0': '🥸',
    12.1: '🧑‍🦰',
    '12.0': '🥱',
    '11.0': '🥰',
  };
  let canvas = null;
  if (supportsOffscreen) {
    canvas = new OffscreenCanvas(100, 30);
  } else {
    canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 30;
  }
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const offset = 12;
  ctx.fillStyle = '#f00';
  ctx.textBaseline = 'top';
  ctx.font = '32px Arial';
  const check = (unicode) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillText(unicode, 0, 0);
    return (
      ctx.getImageData(offset, offset, 1, 1).data[0] !== 0 &&
      ctx.getImageData(offset + 44, offset, 1, 1).data[0] === 0
    );
  };
  for (const version of Object.keys(emoji)) {
    if (check(emoji[version])) {
      return version;
    }
  }
  return '5.0';
};

const htmlToMarkdown = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const convert = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const tag = node.tagName.toLowerCase();

    switch (tag) {
      case 'p':
        return '\n\n' + getChildren(node).trim() + '\n\n';
      case 'div':
        return '\n' + getChildren(node).trim() + '\n';
      case 'br':
        return '\n';
      case 'strong':
      case 'b':
        return '**' + getChildren(node).trim() + '**';
      case 'em':
      case 'i':
        return '_' + getChildren(node).trim() + '_';
      case 'blockquote':
        return (
          '\n' +
          getChildren(node)
            .split('\n')
            .map((line) => '> ' + line)
            .join('\n') +
          '\n'
        );
      case 'a': {
        const href = node.getAttribute('href') || '';
        const text = getChildren(node).trim();
        if (href === text) {
          return href;
        }
        return `[${text}](${href})`;
      }
      case 'ul':
        return (
          '\n' +
          Array.from(node.children)
            .map((li) => '- ' + convert(li).trim())
            .join('\n') +
          '\n'
        );
      case 'ol':
        return (
          '\n' +
          Array.from(node.children)
            .map((li, i) => `${i + 1}. ` + convert(li).trim())
            .join('\n') +
          '\n'
        );
      case 'li':
        return getChildren(node);
      case 'img': {
        const src = node.getAttribute('src') || '';
        const alt = node.getAttribute('alt') || '';
        return `![${alt}](${src})`;
      }
      case 'pre': {
        const codeText = node.textContent.trim();
        return `\n\n\`\`\`\n${codeText}\n\`\`\`\n\n`;
      }
      case 'code': {
        const codeInline = node.textContent.trim();
        return '`' + codeInline + '`';
      }
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6': {
        const level = parseInt(tag[1], 10);
        return (
          '\n\n' + '#'.repeat(level) + ' ' + getChildren(node).trim() + '\n\n'
        );
      }
      case 'span':
        return getChildren(node); // treat as transparent
      default:
        return getChildren(node); // fallback
    }
  };

  const getChildren = (parent) => {
    return Array.from(parent.childNodes).map(convert).join('');
  };

  const markdown = getChildren(doc.body);
  return markdown.replace(/\n{3,}/g, '\n\n').trim();
};

const useStyles = makeStyles((theme) => ({
  container: {
    background:
      theme.palette.type === 'light'
        ? theme.palette.grey[300]
        : theme.palette.primary.dark,
  },
}));

const ChatInput = ({ onEvent, onClose, isMobile = false }) => {
  const [message, setMessage] = useState('');
  const [settingButtonEl, setSettingButtonEl] = useState(null);
  const [emojiButtonEl, setEmojiButtonEl] = useState(null);
  const [emojiVersion, setEmojiVersion] = useState(null);
  const theme = useTheme();

  const pasteHandler = useCallback((event) => {
    try {
      let pastedText = event.clipboardData.getData('text/html');
      if (!pastedText) {
        return;
      }
      const { target } = event;
      let modifiedText = htmlToMarkdown(pastedText.trim());
      let start = target.selectionStart;
      let end = target.selectionEnd;
      let originalValue = target.value;
      const newValue =
        originalValue.slice(0, start) + modifiedText + originalValue.slice(end);
      let newCursorPos = start + modifiedText.length;
      event.preventDefault();
      setMessage(newValue);
      setTimeout(() => {
        if (document.body.contains(target)) {
          target.focus();
          target.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 150);
    } catch (error) {
      Logger.warn('TextField::onPaste', error);
    }
  }, []);

  const [setInputRef, inputEl] = useEventListenerRef('paste', pasteHandler);

  useEffect(() => {
    let input = inputEl.current;
    if (input) {
      if (!isMobile) {
        input.focus();
      }
      setEmojiVersion(getEmojiVersion());
    }
  }, [isMobile, inputEl]);

  const handleInput = (event) => {
    if ((event.key === 'Escape' || event.key === 'Esc') && onClose) {
      return onClose(event);
    }
    if (event.key === 'Enter') {
      if (message.trim().replace(/\r?\n/g, '') === '') {
        event.preventDefault();
        return;
      }
      if ((isMobile && !event.force) || event.shiftKey) {
        return;
      }
      event.preventDefault();
      onEvent({ type: 'send_chat', content: message.trim() });
      setMessage('');
    }
  };

  const onSettingButtonClick = (event) => {
    setSettingButtonEl(event.currentTarget);
  };

  const closeSettingMenu = () => {
    setSettingButtonEl(null);
  };

  const onEmojiButtonClick = (event) => {
    setEmojiButtonEl(event.currentTarget);
  };

  const closeEmoji = () => {
    setEmojiButtonEl(null);
  };

  const onEmojiClick = ({ emoji }) => {
    const input = inputEl.current;
    closeEmoji();
    if (input) {
      const value = input.value;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const isCursorAtEnd = start === value.length && end === value.length;

      let newMessage;
      let cursorPosition;

      if (isCursorAtEnd || start == null || end == null) {
        // Append emoji to the end
        newMessage = value + emoji;
        cursorPosition = newMessage.length;
      } else {
        // Insert emoji at cursor or replace selection
        newMessage = value.slice(0, start) + emoji + value.slice(end);
        cursorPosition = start + emoji.length;
      }
      setMessage(newMessage);
      setTimeout(() => {
        if (document.body.contains(input)) {
          input.focus();
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 150);
    }
  };

  const onSaveChat = () => {
    onEvent({ type: 'chat_export' });
    closeSettingMenu();
  };

  const showShareLocation = async () => {
    onEvent({ type: 'share_location_dialog' });
    closeSettingMenu();
  };

  const classes = useStyles();

  return (
    <>
      <Box p={2} className={classes.container}>
        <TextField
          fullWidth
          value={message}
          multiline
          maxRows={4}
          color="secondary"
          placeholder={I18n.t('aria:label:chat_input', {
            defaultValue: 'Type a message',
          })}
          inputRef={setInputRef}
          onChange={(event) => {
            setMessage(event.target.value);
          }}
          onKeyDown={handleInput}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {!isMobile && (
                  <IconButton
                    edge="end"
                    onClick={onEmojiButtonClick}
                    aria-controls="emoji-menu"
                    aria-haspopup="true"
                  >
                    <Icon color="action">emoji_emotions</Icon>
                  </IconButton>
                )}
                <IconButton
                  edge="end"
                  onClick={() =>
                    handleInput({
                      key: 'Enter',
                      preventDefault: () => {},
                      force: true,
                    })
                  }
                  aria-label={I18n.t('aria:label:send_message', {
                    defaultValue: 'Send message',
                  })}
                >
                  <Icon color="action">send</Icon>
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={onSettingButtonClick}
                  aria-controls="chat-options-menu"
                  aria-haspopup="true"
                >
                  <Icon color="action">more_vert</Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Menu
        id="chat-options-menu"
        keepMounted
        anchorEl={settingButtonEl}
        open={Boolean(settingButtonEl)}
        onClose={closeSettingMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        <MenuItem onClick={showShareLocation}>
          {I18n.t('label:share_location')}
        </MenuItem>
        {!isMobile && (
          <MenuItem onClick={onSaveChat}>{I18n.t('label:save_chat')}</MenuItem>
        )}
      </Menu>
      <Menu
        id="emoji-menu"
        keepMounted
        anchorEl={emojiButtonEl}
        open={Boolean(emojiButtonEl)}
        onClose={closeEmoji}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        {Boolean(emojiButtonEl) && emojiVersion !== null && (
          <EmojiPicker
            getEmojiUrl={(unified) =>
              `${getBasePath()}images/twemoji/64/${unified}.png`
            }
            onEmojiClick={onEmojiClick}
            lazyLoadEmojis={true}
            emojiVersion={emojiVersion}
            theme={theme.palette.type}
          />
        )}
      </Menu>
    </>
  );
};

ChatInput.propTypes = {
  onEvent: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default ChatInput;
